/**
 * ConflictModal - Displays sync conflicts for user resolution
 * Per spec Section 6.6 (Conflict Resolution UI)
 *
 * Shows when syncing after offline period detects conflicts
 * User chooses: Keep Mine (force update) or Keep Theirs (discard local change)
 */

import { useState } from 'react'
import { useSync } from '../context/SyncContext'

function ConflictModal() {
  const { syncManager, conflictCount } = useSync()
  const [currentIndex, setCurrentIndex] = useState(0)

  const conflicts = syncManager.getConflicts()

  // Don't render if no conflicts
  if (conflicts.length === 0) {
    return null
  }

  const currentConflict = conflicts[currentIndex]
  const { item, conflict } = currentConflict

  // Handle resolution
  const handleResolution = (resolution) => {
    syncManager.resolveConflict(item.id, resolution)

    // Move to next conflict or close
    if (currentIndex >= conflicts.length - 1) {
      setCurrentIndex(0)
    }
  }

  // Format field name for display
  const formatFieldName = (field) => {
    return field
      .replace(/_/g, ' ')
      .replace(/([A-Z])/g, ' $1')
      .trim()
      .split(' ')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ')
  }

  // Format value for display
  const formatValue = (value) => {
    if (value === null || value === undefined) return '(empty)'
    if (typeof value === 'boolean') return value ? 'Yes' : 'No'
    if (typeof value === 'object') return JSON.stringify(value)
    return String(value)
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70">
      <div className="bg-crm-bg-light rounded-xl w-full max-w-sm mx-4 overflow-hidden">
        {/* Header */}
        <div className="bg-status-yellow/20 px-4 py-3 border-b border-crm-bg-lighter">
          <div className="flex items-center gap-2">
            <span className="text-status-yellow text-xl">⚠</span>
            <h2 className="text-lg font-semibold text-white">
              Sync Conflict
            </h2>
          </div>
          {conflicts.length > 1 && (
            <p className="text-xs text-gray-400 mt-1">
              {currentIndex + 1} of {conflicts.length} conflicts
            </p>
          )}
        </div>

        {/* Content */}
        <div className="p-4">
          <p className="text-sm text-gray-400 mb-4">
            While you were offline, {conflict.edited_by || 'another partner'} also
            edited this record.
          </p>

          {/* Entity info */}
          <div className="bg-crm-bg rounded-lg p-3 mb-4">
            <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">
              {item.action.replace(/_/g, ' ')}
            </p>
            <p className="text-white font-medium">
              {conflict.entity_name || item.entity_id}
            </p>
          </div>

          {/* Field comparison */}
          <div className="space-y-3">
            <p className="text-xs text-gray-500 uppercase tracking-wide">
              Field: {formatFieldName(conflict.field)}
            </p>

            {/* Your value */}
            <div className="bg-crm-bg rounded-lg p-3 border-l-4 border-crm-gold">
              <p className="text-xs text-crm-gold mb-1">Your edit:</p>
              <p className="text-white text-sm break-words">
                {formatValue(conflict.your_value)}
              </p>
            </div>

            {/* Their value */}
            <div className="bg-crm-bg rounded-lg p-3 border-l-4 border-status-yellow">
              <p className="text-xs text-status-yellow mb-1">
                {conflict.edited_by || 'Their'} edit:
              </p>
              <p className="text-white text-sm break-words">
                {formatValue(conflict.their_value)}
              </p>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="px-4 pb-4 space-y-2">
          <button
            onClick={() => handleResolution('keep_mine')}
            className="w-full py-3 bg-crm-gold text-crm-bg font-semibold rounded-lg
                       hover:bg-crm-gold-light active:bg-crm-gold-dark
                       transition-colors"
          >
            Keep My Edit
          </button>

          <button
            onClick={() => handleResolution('keep_theirs')}
            className="w-full py-3 bg-crm-bg-lighter text-white font-medium rounded-lg
                       border border-crm-bg-lighter hover:border-gray-500
                       transition-colors"
          >
            Keep Their Edit
          </button>

          {/* View full record button (optional) */}
          {conflict.entity_url && (
            <button
              onClick={() => window.location.href = conflict.entity_url}
              className="w-full py-2 text-gray-400 text-sm hover:text-white
                         transition-colors"
            >
              View Full Record
            </button>
          )}
        </div>

        {/* Multiple conflicts navigation */}
        {conflicts.length > 1 && (
          <div className="px-4 pb-4 flex justify-between">
            <button
              onClick={() => setCurrentIndex((i) => Math.max(0, i - 1))}
              disabled={currentIndex === 0}
              className="text-sm text-gray-400 hover:text-white disabled:opacity-50"
            >
              ← Previous
            </button>
            <button
              onClick={() => setCurrentIndex((i) => Math.min(conflicts.length - 1, i + 1))}
              disabled={currentIndex === conflicts.length - 1}
              className="text-sm text-gray-400 hover:text-white disabled:opacity-50"
            >
              Next →
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

export default ConflictModal
